/**
 * The two-step verification setup steps, shared by the forced setup screen
 * (MfaEnrollPage) and the Two-Step Verification card in User Preferences:
 * start, add the key to an authenticator app, confirm a code, then see the
 * recovery codes once. The secret is shown here once, to the account holder.
 */

import { useState, type ReactNode } from 'react'
import { ApiError, request } from '@/lib/api'

export function MfaSetup({ intro, doneLabel, onDone }: { intro: ReactNode; doneLabel: string; onDone: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const start = async () => {
    setError(null)
    try { setSetup(await request<{ secret: string; otpauthUri: string }>('/auth/mfa-enroll', { method: 'POST', body: {} })) } catch (e) { setError(e instanceof ApiError ? e.message : 'Setup could not start.') }
  }
  const confirm = async () => {
    setError(null)
    try { setCodes((await request<{ recoveryCodes: string[] }>('/auth/mfa-confirm', { method: 'POST', body: { code } })).recoveryCodes) } catch (e) { setError(e instanceof ApiError ? e.message : 'That code did not work.') }
  }
  return (
    <>
      {codes ? (
        <>
          <p>Two-step verification is on. Keep these recovery codes somewhere safe — each works once if you lose your phone. They will not be shown again.</p>
          <ul className="mfa-codes">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ul>
          <button type="button" className="button button--primary" onClick={onDone}>{doneLabel}</button>
        </>
      ) : !setup ? (
        <>
          {intro}
          <button type="button" className="button button--primary" onClick={() => void start()}>Start setup</button>
        </>
      ) : (
        <>
          <p>Add this account to your authenticator app. On a phone, tap the link; otherwise enter the key by hand.</p>
          <p><a href={setup.otpauthUri}>Open in an authenticator app</a></p>
          <p>Setup key: <code style={{ wordBreak: 'break-all' }}>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code></p>
          <label>Code from the app <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" /></label>
          <p><button type="button" className="button button--primary" disabled={code.trim().length < 6} onClick={() => void confirm()}>Turn on</button></p>
        </>
      )}
      {error && <p className="support__error" role="alert">{error}</p>}
    </>
  )
}
